/**
 * ============================================================================
 * DISPUTES PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for listing all user disputes
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../hooks/useDispute';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Select from '../components/common/Select';
import Input from '../components/common/Input';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';
import Avatar from '../components/common/Avatar';
import Tabs from '../components/common/Tabs';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Disputes Page Component
 * @returns {JSX.Element}
 */
const Disputes = () => {
  const navigate = useNavigate();
  const { listDisputes, loading } = useDispute();
  const { showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [disputes, setDisputes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadDisputes();
  }, [filter, page]);

  const loadDisputes = async () => {
    try {
      setIsLoading(true);
      const response = await listDisputes({
        page,
        limit: 20,
        status: filter !== 'all' ? filter : undefined,
        search: searchQuery || undefined,
      });

      const data = response?.disputes || response?.data || response || [];
      setDisputes(data);
      setHasMore(data.length === 20);
      
      if (response?.summary) {
        setStats(response.summary);
      }
    } catch (error) {
      showError(language === 'fr' ? 'Échec du chargement des litiges' : 'Failed to load disputes');
      console.error('Load disputes error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadDisputes();
  };

  const getDisputeIcon = (issueType) => {
    const icons = {
      non_delivery: '📦',
      damaged: '💔',
      wrong_item: '🔄',
      quality: '⭐',
      other: '❓',
    };
    return icons[issueType] || '⚠️';
  };

  const getIssueTypeName = (issueType) => {
    const names = {
      non_delivery: language === 'fr' ? 'Non-livraison' : 'Non-Delivery',
      damaged: language === 'fr' ? 'Article endommagé' : 'Damaged Item',
      wrong_item: language === 'fr' ? 'Mauvais article' : 'Wrong Item',
      quality: language === 'fr' ? 'Problème de qualité' : 'Quality Issue',
      other: language === 'fr' ? 'Autre' : 'Other',
    };
    return names[issueType] || issueType;
  };

  const filterTabs = [
    { id: 'all', label: language === 'fr' ? 'Tous' : 'All' },
    { id: 'open', label: language === 'fr' ? 'Ouverts' : 'Open' },
    { id: 'negotiating', label: language === 'fr' ? 'En négociation' : 'Negotiating' },
    { id: 'resolved', label: language === 'fr' ? 'Résolus' : 'Resolved' },
    { id: 'escalated', label: language === 'fr' ? 'Escaladés' : 'Escalated' },
  ];

  const statusOptions = [
    { value: 'all', label: language === 'fr' ? 'Tous les statuts' : 'All Statuses' },
    { value: 'open', label: language === 'fr' ? 'Ouvert' : 'Open' },
    { value: 'negotiating', label: language === 'fr' ? 'En négociation' : 'Negotiating' },
    { value: 'resolved', label: language === 'fr' ? 'Résolu' : 'Resolved' },
    { value: 'escalated', label: language === 'fr' ? 'Escaladé' : 'Escalated' },
  ];

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
              ⚠️ {language === 'fr' ? 'Litiges' : 'Disputes'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {language === 'fr'
                ? 'Gérez tous vos litiges et négociations'
                : 'Manage all your disputes and negotiations'}
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/disputes/raise')}
            className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
          >
            <span className="text-xl mr-2">+</span>
            {language === 'fr' ? 'Créer un litige' : 'Raise Dispute'}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <Card
              variant="glass"
              className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-yellow-50/80 to-orange-50/80 dark:from-yellow-900/20 dark:to-orange-900/20"
              hover
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-2xl shadow-lg shadow-yellow-500/30 animate-float">
                  ⚠️
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    {language === 'fr' ? 'Ouverts' : 'Open'}
                  </p>
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {stats.open || 0}
                  </p>
                </div>
              </div>
            </Card>
            <Card
              variant="glass"
              className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 dark:from-blue-900/20 dark:to-cyan-900/20"
              hover
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl">
                  💬
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    {language === 'fr' ? 'En négociation' : 'Negotiating'}
                  </p>
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {stats.negotiating || 0}
                  </p>
                </div>
              </div>
            </Card>
            <Card
              variant="glass"
              className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-green-50/80 to-emerald-50/80 dark:from-green-900/20 dark:to-emerald-900/20"
              hover
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl shadow-lg shadow-green-500/30 animate-float" style={{ animationDelay: '0.2s' }}>
                  ✅
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    {language === 'fr' ? 'Résolus' : 'Resolved'}
                  </p>
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {stats.resolved || 0}
                  </p>
                </div>
              </div>
            </Card>
            <Card
              variant="glass"
              className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-orange-50/80 to-red-50/80 dark:from-orange-900/20 dark:to-red-900/20"
              hover
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-2xl shadow-lg shadow-orange-500/30 animate-float" style={{ animationDelay: '0.3s' }}>
                  🔥
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    {language === 'fr' ? 'Escaladés' : 'Escalated'}
                  </p>
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {stats.escalated || 0}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'fr' ? 'Rechercher des litiges...' : 'Search disputes...'}
              className="w-full"
            />
          </div>
          <div className="sm:w-48">
            <Select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
              options={statusOptions}
            />
          </div>
          <Button type="submit" variant="primary">
            {language === 'fr' ? 'Rechercher' : 'Search'}
          </Button>
          {(searchQuery || filter !== 'all') && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchQuery('');
                setFilter('all');
                setPage(1);
              }}
            >
              {language === 'fr' ? 'Effacer' : 'Clear'}
            </Button>
          )}
        </form>
      </Card>

      {/* Filter Tabs */}
      <Card
        variant="glass"
        className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
        style={{ animationDelay: '0.3s' }}
      >
        <Tabs
          tabs={filterTabs}
          activeTab={filter}
          onTabChange={(tabId) => {
            setFilter(tabId);
            setPage(1);
          }}
          variant="pills"
        />
      </Card>

      {/* Disputes List */}
      {isLoading ? (
        <Card variant="glass" className="p-12">
          <div className="flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : disputes.length === 0 ? (
        <Card variant="glass" className="p-12 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <EmptyState
            icon="⚠️"
            title={language === 'fr' ? 'Aucun litige' : 'No disputes'}
            description={
              language === 'fr'
                ? 'Vous n\'avez pas encore de litiges'
                : "You don't have any disputes yet"
            }
            action={
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/disputes/raise')}
                className="bg-gradient-to-r from-blue-600 to-green-600"
              >
                {language === 'fr' ? 'Créer un litige' : 'Raise a Dispute'}
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          {disputes.map((dispute, index) => (
            <Card
              key={dispute.id || dispute.disputeId || index}
              variant="glass"
              hover
              clickable
              onClick={() => navigate(`/disputes/${dispute.id || dispute.disputeId}`)}
              className="animate-fadeInUp"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-2xl shadow-lg">
                    {getDisputeIcon(dispute.issueType)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-black text-gray-900 dark:text-white">
                        {dispute.title || dispute.issueType || (language === 'fr' ? 'Litige' : 'Dispute')}
                      </h3>
                      <StatusBadge
                        variant={
                          dispute.status === 'resolved'
                            ? 'success'
                            : dispute.status === 'negotiating'
                            ? 'primary'
                            : dispute.status === 'escalated'
                            ? 'warning'
                            : 'default'
                        }
                        size="sm"
                      >
                        {dispute.status}
                      </StatusBadge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span className="font-medium">
                        {getIssueTypeName(dispute.issueType)}
                      </span>
                      {dispute.transactionTitle && (
                        <>
                          <span>•</span>
                          <span>{dispute.transactionTitle}</span>
                        </>
                      )}
                      {dispute.transactionReference && (
                        <>
                          <span>•</span>
                          <span className="font-mono">#{dispute.transactionReference}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      {dispute.openedAt && (
                        <span>
                          {language === 'fr' ? 'Ouvert le' : 'Opened'} {formatDate(dispute.openedAt)}
                        </span>
                      )}
                      {dispute.counterpartyName && (
                        <>
                          <span>•</span>
                          <span>
                            {language === 'fr' ? 'Avec' : 'With'} {dispute.counterpartyName}
                          </span>
                        </>
                      )}
                      {dispute.unreadMessages > 0 && (
                        <span className="px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-bold">
                          {dispute.unreadMessages} {language === 'fr' ? 'nouveau(x)' : 'new'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <svg
                  className="w-6 h-6 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="mt-6 text-center animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => prev + 1)}
            loading={loading}
          >
            {language === 'fr' ? 'Charger plus' : 'Load More'}
          </Button>
        </div>
      )}
    </UserDashboardLayout>
  );
};

export default Disputes;
